/**
 * InviteManagerPanel.jsx — Owner-only widget for adding co-managers by
 * email address, even before the invited person has signed up. If the
 * email belongs to an existing verified user we promote inline; else
 * the invite waits in `family_manager_invites` and is redeemed on the
 * first sign-in from that address.
 */
import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";

export default function InviteManagerPanel({ treeId }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return toast.error("Enter an email address");
    setBusy(true); setLastResult(null);
    try {
      const { data } = await http.post(`/family-trees/${treeId}/roles/invite-by-email`, {
        email: email.trim(),
        role,
        note: note.trim() || null,
      });
      setLastResult(data);
      if (data.mode === "promoted_existing") {
        toast.success(`Promoted to ${role} — they now have access.`);
      } else {
        toast.success("Invitation sent. They'll be auto-promoted on first sign-in.");
      }
      setEmail(""); setNote("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send invite");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="invite-manager-panel">
      <div className="overline mb-3">APPOINT A CO-MANAGER</div>
      <p className="text-xs opacity-70 mb-3">
        Invite your spouse, sibling, or child to co-manage this tree. If their email is not yet on IndianFamilyTree, we'll wait — they're auto-promoted the moment they sign in with that email.
      </p>
      <form onSubmit={submit} className="rounded-xl border p-3 space-y-2" style={{ borderColor: "var(--wofy-line)", background: "var(--wofy-surface)" }}>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="e.g. spouse@example.com"
            required
            className="rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: "var(--wofy-line)" }}
            data-testid="invite-manager-email"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: "var(--wofy-line)" }}
            data-testid="invite-manager-role"
          >
            <option value="editor">Editor · can edit members</option>
            <option value="admin">Co-Admin · full control</option>
          </select>
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder="Optional personal note included in the invite email…"
          className="w-full rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "var(--wofy-line)" }}
          data-testid="invite-manager-note"
        />
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={busy}
            className="wofy-btn-primary text-sm"
            data-testid="invite-manager-send"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            {busy ? "Sending…" : "Send invite"}
          </button>
        </div>
      </form>
      {lastResult && (
        <div className="mt-2 text-xs opacity-70" data-testid="invite-manager-result">
          {lastResult.mode === "promoted_existing"
            ? "✔ Existing user promoted immediately."
            : "✔ Invite queued — they'll be auto-promoted on their next sign-in from this email."}
        </div>
      )}
    </section>
  );
}
